import { Flex, Spin } from 'antd'
import isUndefined from 'lodash-es/isUndefined'
import React, { forwardRef, ReactNode, useState } from 'react'
import { useIntersection, useInterval, useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import KolDetailDataBlockHeader, {
  KolDetailDataBlockHeaderProps,
} from '@/components/kol/common/kol-detail-data-block-header'
import { ampli, StayKolDetailProperties } from '@/utils/ampli'
import { PlatformShortcode } from '@/utils/convert-platform'
import useEnsuredForwardedRef from '@/utils/hooks/use-ensured-forwarded-ref'

const MAXIMUM_STAY_SECOND = 300

interface KolDetailDataBlockProps {
  header: KolDetailDataBlockHeaderProps
  kolId: StayKolDetailProperties['kolId']
  kolName?: string
  kolMetrics: StayKolDetailProperties['kolMetrics']
  platform?: PlatformShortcode
  colSpan: number // 寬度 ≤ 980 時會強制是 24
  children?: ReactNode
  similarKolIds?: string[]
  similarKolDcHashedIds?: string[]
  similarKolNames?: string[]
  hoverableExtraContent?: boolean
  loading?: boolean
}

const KolDetailDataBlock = forwardRef<HTMLDivElement, KolDetailDataBlockProps>(
  (
    {
      header,
      colSpan,
      children,
      kolId,
      kolMetrics,
      kolName,
      platform,
      similarKolIds,
      similarKolDcHashedIds,
      similarKolNames,
      hoverableExtraContent = false,
      loading,
    },
    ref,
  ) => {
    const [hovered, setHovered] = useState(false)
    const [staySecond, setStaySecond] = useState(0)
    const ensuredForwardRef = useEnsuredForwardedRef(ref)
    const entry = useIntersection(ensuredForwardRef, {
      threshold: 0.5,
    })

    const isIntersecting = entry?.isIntersecting ?? false
    const isReady =
      isIntersecting && !isUndefined(kolName) && !isUndefined(platform)

    const handleMouseEnter: VoidFunction = () => {
      setHovered(true)
    }
    const handleMouseLeave: VoidFunction = () => {
      setHovered(false)
    }

    useInterval(
      () => {
        setStaySecond((staySecond) => staySecond + 1)
      },
      isReady ? 1000 : null,
    )

    useUpdateEffect(() => {
      if (!platform) {
        return
      }

      if (staySecond === 3) {
        ampli.stayKolDetail({
          kolId,
          kolMetrics,
          platform,
          similarKolIds,
          similarKolDcIds: similarKolDcHashedIds,
          similarKolNames,
        })
      }
    }, [staySecond])

    useUpdateEffect(() => {
      if (!isIntersecting && staySecond > 0) {
        ampli.stayKolDetailSection({
          kolMetrics,
          kolName,
          platform,
          second: Math.min(staySecond, MAXIMUM_STAY_SECOND),
          similarKolIds,
          similarKolDcIds: similarKolDcHashedIds,
          similarKolNames,
        })
        setStaySecond(0)
      }
    }, [isIntersecting])

    return (
      <Box
        colSpan={colSpan}
        ref={ensuredForwardRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <KolDetailDataBlockHeader
          {...header}
          hover={{ hoverableExtraContent, hovered }}
        />
        <StatisticBox>
          {loading ? (
            <Flex
              align='center'
              justify='center'
              style={{ height: '100%', minHeight: 140 }}
            >
              <Spin />
            </Flex>
          ) : (
            children
          )}
        </StatisticBox>
      </Box>
    )
  },
)

KolDetailDataBlock.displayName = 'KolDetailDataBlock'

const Box = styled.div<{ colSpan: number }>`
  display: flex;
  flex-direction: column;
  grid-column: span 24 / span 24;
  border-radius: 4px;
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.04);
  background: ${({ theme }): string => theme.colors.white};

  @media (min-width: 981px) {
    grid-column: span ${({ colSpan }): number => colSpan};
  }
`

const StatisticBox = styled.div`
  flex: 1;
  padding: 20px 30px 30px;
`

export default KolDetailDataBlock
